import React, { useState, useEffect } from 'react';
import {
  Modal,
  List,
  Typography,
  Button,
  Input,
  Space,
  Tag,
  message,
} from 'antd';
import { updateGroupMemberDetails } from '../../api/group';

const { Text } = Typography;

const GroupMembersModal = ({ visible, onClose, groupMembers, group, role }) => {
  const [members, setMembers] = useState([]);
  const [savingId, setSavingId] = useState(null);

  // Round 1: anyone can edit
  // Round 2: only admins
  const canEdit =
    group?.round === 1 || (role === 'admin' && group?.round === 2);

  useEffect(() => {
    setMembers(groupMembers || []);
  }, [groupMembers]);

  const handleFieldChange = (index, field, value) => {
    setMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const doSave = async (original, updated, merge = false) => {
    if (!group?._id) return;

    return updateGroupMemberDetails(group._id, original.id_number, {
      name: updated.name,
      id_number: updated.id_number,
      merge,
    });
  };

  const handleSaveMember = async (index) => {
    const original = groupMembers[index];
    const updated = members[index];

    if (!original || !updated) return;

    const nameChanged = original.name !== updated.name;
    const idChanged = original.id_number !== updated.id_number;

    if (!nameChanged && !idChanged) return;

    try {
      setSavingId(original.id_number);

      await doSave(original, updated, false);
      message.success('Member updated');
    } catch (err) {
      // Round 2 merge flow (existing behaviour)
      if (err.status === 409 && err.data && err.data.canMerge) {
        const conflict = err.data.conflict || {};
        let conflictName = 'another profile';
        let conflictExtra = '';

        if (conflict.contestant) {
          const c = conflict.contestant;
          conflictName = `${c.first_name} ${c.last_name}`.trim();
          if (c.contestant_number != null) {
            conflictExtra = ` (contestant #${c.contestant_number})`;
          }
        } else if (conflict.groupMember) {
          const gm = conflict.groupMember;
          conflictName = gm.name || 'another group member';
          if (gm.contestant_number != null) {
            conflictExtra = ` (contestant #${gm.contestant_number})`;
          } else if (gm.group_name) {
            conflictExtra = ` in group "${gm.group_name}"`;
          }
        }

        Modal.confirm({
          title: 'ID number already in use',
          content: (
            <>
              <p>
                This ID number is already used by{' '}
                <strong>
                  {conflictName}
                  {conflictExtra}
                </strong>
                .
              </p>
              <p>
                If you merge, this member will share the same contestant number
                and their invoices and applications will be combined.
              </p>
            </>
          ),
          okText: 'Merge',
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              setSavingId(original.id_number);
              await doSave(original, updated, true);
              message.success('Member merged and updated');
            } catch (mergeErr) {
              console.error(mergeErr);
              message.error(mergeErr.message || 'Failed to merge');
              setMembers(groupMembers);
            } finally {
              setSavingId(null);
            }
          },
          onCancel: () => {
            setMembers(groupMembers);
          },
        });
      }
      // Round 1 conflict or generic "same protections as create group"
      else if (err.status === 409 && err.data && err.data.conflicts) {
        const conflicts = err.data.conflicts || [];

        Modal.error({
          title: 'ID number already in use',
          content: (
            <div>
              <p>
                This ID number is already used by a different contestant in
                another group:
              </p>
              <ul>
                {conflicts.map((c, idx) => (
                  <li key={idx}>
                    ID <strong>{c.id_number}</strong> is used by{' '}
                    <strong>{c.existingMemberName || 'another contestant'}</strong>
                    {c.group_name ? ` in group "${c.group_name}"` : ''}
                    {c.newMemberName ? ` (you entered: ${c.newMemberName})` : ''}
                  </li>
                ))}
              </ul>
              <p>
                If this is the same contestant, please make sure the name you
                enter is exactly the same as the existing one and then try
                again.
              </p>
            </div>
          ),
        });

        // Reset edits back to original values
        setMembers(groupMembers);
      } else {
        console.error(err);
        message.error(err.message || 'Failed to update member');
        setMembers(groupMembers);
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal
      title={
        <>
          Group Members
          {typeof group?.round === 'number' && (
            <Tag style={{ marginLeft: 8 }}>Round {group.round}</Tag>
          )}
        </>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <List
        locale={{ emptyText: 'No Members' }}
        dataSource={members}
        renderItem={(member, index) => (
          <List.Item key={member._id || index}>
            {canEdit ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>Name</Text>
                  <Input
                    value={member.name}
                    onChange={(e) =>
                      handleFieldChange(index, 'name', e.target.value)
                    }
                    style={{ marginTop: 4 }}
                  />
                </div>

                <div>
                  <Text strong>ID Number</Text>
                  <Input
                    value={member.id_number}
                    onChange={(e) =>
                      handleFieldChange(index, 'id_number', e.target.value)
                    }
                    style={{ marginTop: 4 }}
                  />
                </div>

                <Button
                  type="primary"
                  onClick={() => handleSaveMember(index)}
                  loading={savingId === groupMembers[index]?.id_number}
                  disabled={
                    !member.name ||
                    !member.id_number ||
                    (member.name === groupMembers[index]?.name &&
                      member.id_number ===
                        groupMembers[index]?.id_number)
                  }
                >
                  Save Changes
                </Button>
              </Space>
            ) : (
              <>
                <Text strong>{member.name}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  ({member.id_number})
                </Text>
              </>
            )}
          </List.Item>
        )}
      />
      {!canEdit && (
        <Text type="secondary">
          Only admins can edit members for this group.
        </Text>
      )}
    </Modal>
  );
};

export default GroupMembersModal;
